import { InputGroupAddon, InputGroupInput } from '@playbooks/ui/components/input-group'
import { InputProps } from '../types'
import InputGroupWrapper from '../../../components/ui/InputGroupeWrapper'
import { Calendar } from 'lucide-react'
import { parseDate } from '../../../utils/utils'
import formatForDatetimeInput from '../../../utils/formatForDatetimeInput'
import { useRef } from 'react'
import { cn } from '@playbooks/ui/lib/utils'

export default function DatePicker({
  question,
  onChange,
  invalid,
  type,
}: { type: 'date' | 'datetime-local' } & InputProps) {
  const value = (
    type === 'datetime-local'
      ? // DateTime value is stored as UTC string,
        // but the input expects value in wall-clock format (YYYY-MM-DDTHH:MM)
        formatForDatetimeInput(question.value as string)
      : question.value
  ) as string

  const inputRef = useRef<HTMLInputElement>(null)
  const isIframe = (() => {
    if (typeof window === 'undefined') return false
    try {
      return window.self !== window.top
    } catch {
      return true
    }
  })()

  return (
    <InputGroupWrapper className="relative flex items-center">
      <InputGroupInput
        ref={inputRef}
        id={`question-${question.id}`}
        type={type}
        // The operator `??` prevents uncontrolled to controlled input warning
        value={value ?? ''}
        onChange={e => {
          onChange(
            type === 'datetime-local'
              ? // DateTime value should be stored as UTC string,
                // but the input provides the value in wall-clock format (YYYY-MM-DDTHH:MM)
                parseDate(e.target.value)?.toISOString()
              : e.target.value,
          )
        }}
        aria-invalid={invalid}
        required={question.required}
        className={
          isIframe
            ? // We show the calendar picker indicator in iframe context,
              '[&::-webkit-calendar-picker-indicator]:opacity-50'
            : // We hide the calendar picker indicator in non-iframe context,
              // because we provide a custom button to open the date picker
              '[&::-webkit-calendar-picker-indicator]:hidden'
        }
      />
      <InputGroupAddon
        onClick={() => {
          // We don't want to open programmatically the date picker in an iframe, because it will be blocked by browsers
          if (isIframe) return

          try {
            inputRef.current?.showPicker?.()
          } catch {
            // A fallback for Safari version which does not support showPicker,
            // so we focus and click the input instead
            inputRef.current?.focus()
            inputRef.current?.click()
          }
        }}
        className={cn(
          'absolute right-3 top-0.5 text-foreground focus:border-2',
          // We disable pointer events on the calendar icon in iframe context,
          // because we don't want to open programmatically the date picker in an iframe,
          // because it will be blocked by browsers
          // the pointer-events-none to pass the click event through to the button
          // to be able to click the native calendar-picker-indicator
          isIframe && 'pointer-events-none',
        )}
      >
        <Calendar />
      </InputGroupAddon>
    </InputGroupWrapper>
  )
}
